import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {Paper} from 'src/design/atoms/Paper';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import {UnstyledButton} from 'src/design/atoms/UnstyledButton';
import {useMediaQuery} from '@mantine/hooks';
import React, {useState} from 'react';
import type {ReactNode} from 'react';
import type {InspectionSimpleResponse} from 'src/api/ProactApiClient';
import {InspectionStateEnum} from 'src/api/ProactApiClient';
import {CompletionProgress} from 'src/design/atoms/CompletionProgress';
import {Pill} from 'src/design/atoms/Pill';
import {useIsInspectionDownloaded} from 'src/app/inspections/offline/useInspectionOfflineSession';
import {InspectionStateEnumPill} from 'src/shared/constants/PillConstants';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {formatUtcDateTime, formatUtcShortDate} from 'src/shared/utils/formatters';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface InspectionCardProps {
  inspection: InspectionSimpleResponse;
  /** Whether the current user is this inspection's assignee — the only card state that branches into the guided My Inspections flow rather than the detail view. */
  isAssignedToMe: boolean;
  /** Which surface the card lives on. 'manage' always navigates to the detail view and enables the unassigned highlight; defaults to 'my'. */
  origin?: 'my' | 'manage';
  /** Row actions (View/Edit/Delete) rendered outside the click overlay so they stay valid, non-nested buttons. */
  actions?: ReactNode;
  /** Assignee display or assign anchor, rendered in the body outside the click overlay. */
  assignSlot?: ReactNode;
}

export const InspectionCard: React.FC<InspectionCardProps> = ({
  inspection,
  isAssignedToMe,
  origin = 'my',
  actions,
  assignSlot,
}) => {
  const navigate = useAppNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const isDownloaded = useIsInspectionDownloaded(inspection.id);
  const isWide = useMediaQuery('(min-width: 48em)');

  const navTarget =
    origin === 'manage'
      ? PathUtility.InspectionsView(inspection.id)
      : isAssignedToMe &&
          (inspection.state === InspectionStateEnum.Scheduled ||
            inspection.state === InspectionStateEnum.InProgress ||
            inspection.state === InspectionStateEnum.Completed)
        ? PathUtility.InspectionsMy(inspection.id)
        : PathUtility.InspectionsView(inspection.id);

  const dueLabel = formatUtcShortDate(inspection.dueAt ?? undefined);
  const scheduledLabel = inspection.scheduledStartAt
    ? formatUtcDateTime(inspection.scheduledStartAt)
    : undefined;

  const isUnassignedHighlight =
    origin === 'manage' &&
    !inspection.assigneeId &&
    inspection.state !== InspectionStateEnum.Completed;

  const clientAddressLine = [inspection.clientName, inspection.siteAddress]
    .filter(Boolean)
    .join(' · ');

  const chipLabel = inspection.siteNumber
    ? `${inspection.siteName} · #${inspection.siteNumber}`
    : inspection.siteName;

  const cardLabel = [inspection.siteName, inspection.inspectionTypeName]
    .filter(Boolean)
    .join(' — ');

  // Interactive controls sit above the full-card click overlay; stopPropagation
  // keeps their clicks from also triggering navigation.
  const stop = (node: ReactNode) => (
    <Box style={{position: 'relative', zIndex: 1}} onClick={e => e.stopPropagation()}>
      {node}
    </Box>
  );

  const pillCluster = (
    <Group gap={0} align="center" wrap="nowrap" style={{flexShrink: 0}}>
      <Pill {...InspectionStateEnumPill(inspection.state)} />
      {isDownloaded && <Pill text="Downloaded" color="teal" />}
    </Group>
  );

  const titleElement = (
    <Title order={5} lineClamp={2} style={{minWidth: 0}}>
      {inspection.siteName}
    </Title>
  );

  const progressCluster = (
    <Group gap="sm" wrap="nowrap" style={{flexShrink: 0}}>
      <CompletionProgress
        completed={inspection.terminalDeviceCount}
        total={inspection.scopedDeviceCount}
      />
      {actions && stop(actions)}
    </Group>
  );

  return (
    <Paper
      withBorder
      pos="relative"
      p={0}
      shadow={isHovered ? 'sm' : 'xs'}
      bg={isUnassignedHighlight ? 'yellow.0' : undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        borderColor: isHovered
          ? 'var(--mantine-primary-color-filled)'
          : isUnassignedHighlight
            ? 'var(--mantine-color-yellow-4)'
            : 'var(--mantine-color-gray-3)',
        transition: 'all 0.15s ease',
      }}>
      <UnstyledButton
        onClick={() => navigate(navTarget)}
        aria-label={cardLabel}
        style={{position: 'absolute', inset: 0, zIndex: 0}}
      />
      <Box p="md" style={{borderBottom: '1px solid var(--mantine-color-gray-2)'}}>
        {/* Below sm the title owns its own row: sharing one line with the pill
            and the progress bar leaves too little width to wrap on a word. */}
        {isWide ? (
          <Group justify="space-between" align="center" gap="sm" wrap="nowrap">
            <Group
              gap="sm"
              align="center"
              wrap="nowrap"
              style={{minWidth: 0, flex: 1}}>
              {pillCluster}
              {titleElement}
            </Group>
            {progressCluster}
          </Group>
        ) : (
          <Stack gap="xs">
            <Group justify="space-between" align="center" gap="sm" wrap="nowrap">
              {pillCluster}
              {progressCluster}
            </Group>
            {titleElement}
          </Stack>
        )}
      </Box>
      <Box p="md">
        <Stack gap={4} ta="left">
          <Pill text={chipLabel} />
          {clientAddressLine && (
            <Text size="sm" c="dimmed">
              {clientAddressLine}
            </Text>
          )}
          {scheduledLabel && (
            <Text size="sm" c="dimmed">
              Scheduled {scheduledLabel}
            </Text>
          )}
          <Text size="sm" c="dimmed">
            Due {dueLabel} · {inspection.scopedDeviceCount} devices
          </Text>
          {assignSlot && stop(assignSlot)}
        </Stack>
      </Box>
    </Paper>
  );
};
